/**
 * Prerender entry script for vite-prerender-plugin.
 * This script is used only during the build process to generate static HTML
 * for the 6 public marketing routes. It is NOT included in the client bundle.
 *
 * Uses renderToString with StaticRouter so the prerender pass never touches
 * browser globals (window/document/localStorage).
 */
import React, { Suspense } from 'react';
import { renderToString } from 'react-dom/server';
import { StaticRouter } from 'react-router-dom/server';
import { HelmetProvider } from 'react-helmet-async';
import type { HelmetServerState } from 'react-helmet-async';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { ThemeProvider } from './components/theme-provider';

// Eager imports for all public route pages — no React.lazy() here.
// The browser bundle still uses lazy() in App.tsx; this entry is build-only.
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import SecurityPrivacyPage from './pages/SecurityPrivacyPage';
import IntegrationsPage from './pages/IntegrationsPage';
import AiImplementationPage from './pages/AiImplementationPage';
import ContactPage from './pages/ContactPage';

import { Routes, Route } from 'react-router-dom';

function PrerenderedApp({ url, helmetContext }: { url: string; helmetContext: object }) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { retry: false, staleTime: Infinity },
    },
  });

  return (
    <HelmetProvider context={helmetContext}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="chytbot-theme">
          <StaticRouter location={url}>
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/security-privacy" element={<SecurityPrivacyPage />} />
                <Route path="/integrations" element={<IntegrationsPage />} />
                <Route path="/ai-implementation" element={<AiImplementationPage />} />
                <Route path="/contact" element={<ContactPage />} />
              </Routes>
            </Suspense>
          </StaticRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

// Extract text content from an HTML string like "<title>Foo</title>" → "Foo"
function extractTagText(html: string, tag: string): string {
  const match = html.match(new RegExp(`<${tag}[^>]*>([^<]*)</${tag}>`));
  return match ? match[1] : '';
}

// Parse an HTML attribute string like 'name="description" content="Foo"' into { name, content }
function parseAttrs(attrStr: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /(\w[\w-]*)="([^"]*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(attrStr)) !== null) {
    attrs[m[1]] = m[2];
  }
  return attrs;
}

export async function prerender({ url }: { url: string }) {
  const helmetContext: { helmet?: HelmetServerState } = {};
  const html = renderToString(<PrerenderedApp url={url} helmetContext={helmetContext} />);

  const { helmet } = helmetContext;
  if (!helmet) return { html };

  // Convert helmet output to vite-prerender-plugin's head object format:
  // { title: string, elements: Set<{ type, props }> }
  const titleStr = helmet.title.toString();
  const title = extractTagText(titleStr, 'title');

  const elements = new Set<{ type: string; props: Record<string, string> }>();

  // Parse <meta> tags from helmet
  const metaHtml = helmet.meta.toString();
  const metaRe = /<meta([^/]*)\/?>/g;
  let m: RegExpExecArray | null;
  while ((m = metaRe.exec(metaHtml)) !== null) {
    elements.add({ type: 'meta', props: parseAttrs(m[1]) });
  }

  // Parse <link> tags from helmet (use [^>]* to handle URLs containing slashes)
  const linkHtml = helmet.link.toString();
  const linkRe = /<link((?:[^>]|(?:<[^>]*>))*?)\s*\/?>/g;
  while ((m = linkRe.exec(linkHtml)) !== null) {
    const props = parseAttrs(m[1]);
    if (Object.keys(props).length > 0) {
      elements.add({ type: 'link', props });
    }
  }

  // Parse <script> tags (JSON-LD) from helmet
  const scriptHtml = helmet.script.toString();
  const scriptRe = /<script([^>]*)>([\s\S]*?)<\/script>/g;
  while ((m = scriptRe.exec(scriptHtml)) !== null) {
    const props = parseAttrs(m[1]);
    if (props.type === 'application/ld+json') {
      elements.add({ type: 'script', props: { ...props, children: m[2] } });
    }
  }

  return { html, head: { title, elements } };
}
